import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Table from "../../components/Table";
import Button from "../../components/Buttons/Button";
import Select from "../../components/Select";
import {
  downloadStudentLogsXlsx,
  fetchStudentLogs,
} from "../../services/studentService/studentService";
import {
  StudentActivityChannel,
  StudentCourseActionLog,
} from "../../services/studentService/studentService.types";
import {
  PageContainer,
  ContentWrapper,
  Header,
  TitleBlock,
  Title,
  HeaderActions,
} from "./StudentLogsPage.styles";
import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";

import { useNotify } from "context/NotificationContext";
import { logger } from "utils/logger";
import { useListPage } from "hooks/useListPage";

const rowAmountOptions = [
  { label: "10 записей", value: 10 },
  { label: "20 записей", value: 20 },
  { label: "50 записей", value: 50 },
];

const channelOptions = [
  { label: "Все каналы", value: "" },
  { label: "Web", value: "web" },
  { label: "Telegram", value: "telegram" },
  { label: "Администратор", value: "admin" },
  { label: "Система", value: "system" },
  { label: "Legacy", value: "legacy" },
];

const StudentLogsPage: React.FC = () => {
  const { studentId, courseId } = useParams();
  const location = useLocation();
  const toast = useNotify();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const courseNameFromQuery = searchParams.get("courseName");
  const studentNameFromQuery = searchParams.get("studentName");
  const studentUsernameFromQuery = searchParams.get("studentUsername");

  const [resolvedCourseName, setResolvedCourseName] = useState<string | null>(courseNameFromQuery);
  const [resolvedStudentName, setResolvedStudentName] = useState<string | null>(studentNameFromQuery);
  const [resolvedStudentUsername, setResolvedStudentUsername] = useState<string | null>(studentUsernameFromQuery);
  const [channel, setChannel] = useState<StudentActivityChannel | "">("");
  const previousChannel = useRef<StudentActivityChannel | "">("");

  const {
    items: logs,
    loading,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    orderBy: ordering,
    handleSort,
    reload,
  } = useListPage<StudentCourseActionLog, Awaited<ReturnType<typeof fetchStudentLogs>>>({
    fetcher: ({ page, pageSize: size, orderBy }) =>
      fetchStudentLogs({
        studentId: String(studentId),
        courseId: String(courseId),
        page,
        pageSize: size,
        ordering: orderBy,
        channel,
      }),
    select: (response) => ({ items: response.results, total: response.count }),
    onLoaded: (response) => {
      const firstLog = response.results[0];
      if (!firstLog) return;
      setResolvedCourseName((prev) => prev || firstLog.course_name);
      setResolvedStudentName(
        (prev) => prev || firstLog.student_name || String(firstLog.student_tg_id),
      );
      setResolvedStudentUsername((prev) => prev || firstLog.student_tg_username || null);
    },
    errorMessage: "Ошибка загрузки логов ученика",
    initialPageSize: 10,
    initialOrderBy: "-created",
    enabled: Boolean(studentId && courseId),
  });

  useEffect(() => {
    if (previousChannel.current === channel) return;
    previousChannel.current = channel;
    if (currentPage !== 1) {
      setCurrentPage(1);
      return;
    }
    void reload();
  }, [channel, currentPage, reload, setCurrentPage]);

  const headers = [
    { label: "Дата и время", key: "created", fieldName: "created" },
    { label: "Канал", key: "channel", fieldName: "channel" },
    { label: "Действие", key: "action", fieldName: "action" },
    { label: "Номер урока", key: "lesson_number", fieldName: "lesson_number" },
    { label: "Название урока", key: "lesson_title", fieldName: "lesson_title" },
    { label: "Название ДЗ", key: "assignment_title", fieldName: "assignment_title" },
    { label: "Имя файла", key: "material_filename", fieldName: "material_filename" },
  ];

  const handleDownload = async () => {
    if (!studentId || !courseId) {
      return;
    }

    try {
      const blob = await downloadStudentLogsXlsx({
        studentId,
        courseId,
        ordering,
        channel,
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const safeStudent = resolvedStudentName || `student_${studentId}`;
      const safeCourse = resolvedCourseName || `course_${courseId}`;
      link.download = `logs_${safeStudent}_${safeCourse}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      logger.error("Ошибка при скачивании логов ученика:", error);
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось скачать отчёт",
        life: 4000,
      });
    }
  };

  const rows = logs.map((log) => ({
    created: new Date(log.created).toLocaleString(),
    channel: log.channel_label,
    action: log.action,
    lesson_number: log.lesson_number ?? "-",
    lesson_title: log.lesson_title ?? "-",
    assignment_title: log.assignment_title ?? "-",
    material_filename: log.material_filename ?? "-",
  }));

  const breadcrumbParts: string[] = [];
  if (resolvedStudentName) {
    breadcrumbParts.push(`Студент: ${resolvedStudentName}`);
  }
  if (studentId) {
    breadcrumbParts.push(`TG ID: ${studentId}`);
  }
  if (resolvedStudentUsername) {
    const formattedNickname = resolvedStudentUsername.startsWith("@")
      ? resolvedStudentUsername
      : `@${resolvedStudentUsername}`;
    breadcrumbParts.push(`Ник в TG: ${formattedNickname}`);
  }
  if (resolvedCourseName) {
    breadcrumbParts.push(`Курс: ${resolvedCourseName}`);
  }
  const breadcrumbCurrentLabel = breadcrumbParts.join(" • ") || "Действия ученика";

  return (
    <PageContainer>
      <Sidebar />
      <ContentWrapper>
        <Header>
          <TitleBlock>
            <Breadcrumbs
              items={[
                { label: "Ученики", to: "/students" },
                { label: breadcrumbCurrentLabel },
              ]}
            />
            <Title>Действия ученика</Title>
          </TitleBlock>
          <HeaderActions>
            <Select
              options={channelOptions}
              value={channel}
              onChange={(value) => setChannel(String(value) as StudentActivityChannel | "")}
              ariaLabel="Фильтр по каналу активности"
              minWidth="190px"
            />
            <Button
              type="button"
              variant="primary"
              onClick={handleDownload}
              size="lg"
            >
              Скачать в Excel
            </Button>
          </HeaderActions>
        </Header>
        <Table
          emptyState={{
            title: "Записей пока нет",
            description:
              "Здесь отображается история действий ученика.",
          }}
          headers={headers}
          rows={rows}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          onSort={handleSort}
          rowAmountOptions={rowAmountOptions}
          loading={loading}
        />
      </ContentWrapper>
    </PageContainer>
  );
};

export default StudentLogsPage;
